import { FC } from 'react';
import { Image, Stack } from '@chakra-ui/react';

import physicalPremiumRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/premium.png';
import virtualRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/virtual.png';
import virtualBudgetRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/virtualBudget.png';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import PhysicalCardQuotaReachedFaq from 'features/PaymentCards/components/PhysicalCardQuotaReachedFaq';
import { PREMIUM_CARD_NAME } from 'helpers/paymentCards/constants';
import * as Layout from 'layouts/StepperLayout';
import { PaymentCardType } from 'types/creditCard';

import * as routes from '../../../../routes';
import usePaymentCardRequest from '../../hooks/usePaymentCardRequest';
import { useRequestNewCardContext } from '../../hooks/useRequestNewCardContext';
import locales from './locales';

const SelectCard: FC<StepProps> = ({ goNext }) => {
  const { setSharedState, sharedState } = useRequestNewCardContext();

  const {
    canRequestPhysicalCard,
    error,
    firstPhysicalActivePaymentCardNumber,
    loading,
  } = usePaymentCardRequest();

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView />;
  }

  const handleNext = (
    cardType: PaymentCardType,
    options?: { isBudgetCard: boolean },
  ) => {
    const isBudgetCard = options?.isBudgetCard ?? false;
    setSharedState((prevState) => ({
      ...prevState,
      cardType,
      isBudgetCard,
    }));

    logEvent({
      name: 'Card Request Type Selected',
      properties: {
        cardType,
        companyUserId: sharedState.companyUserId,
        isBudgetCard,
      },
      usePrefix: false,
    });

    goNext();
  };

  return (
    <Layout.Root>
      <TrackView
        eventProperties={{
          companyUserId: sharedState.companyUserId,
        }}
        pageName="Card Request Started"
        usePrefix={false}
      />

      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton to={routes.root} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Stack marginBottom="space-32" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text size="md">{locales.description}</Text>
          </Stack>

          <SunshineCard.Group border="none">
            <SunshineCard
              alignItems="start"
              onClick={() =>
                handleNext(PaymentCardType.VIRTUAL, { isBudgetCard: true })
              }
              title={locales.options[PaymentCardType.VIRTUAL].budget.title}
            >
              <SunshineCard.Slot name="aside">
                <Image
                  alt=""
                  role="presentation"
                  src={virtualBudgetRebrandedPaymentCardImg}
                  width={110}
                />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.options[PaymentCardType.VIRTUAL].budget.description}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineTag colorScheme="orange" icon="flash" size="sm">
                  {locales.badge}
                </SunshineTag>

                <SunshineIcon
                  color="grey.600"
                  name="chevron-right"
                  size="icon-20"
                />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="start"
              onClick={() => handleNext(PaymentCardType.VIRTUAL)}
              title={locales.options[PaymentCardType.VIRTUAL].default.title}
            >
              <SunshineCard.Slot name="aside">
                <Image
                  alt=""
                  role="presentation"
                  src={virtualRebrandedPaymentCardImg}
                  width={110}
                />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.options[PaymentCardType.VIRTUAL].default.description}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineTag colorScheme="orange" icon="flash" size="sm">
                  {locales.badge}
                </SunshineTag>

                <SunshineIcon
                  color="grey.600"
                  name="chevron-right"
                  size="icon-20"
                />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="start"
              isDisabled={!canRequestPhysicalCard}
              onClick={() => handleNext(PaymentCardType.PHYSICAL)}
              title={
                locales.formatString(
                  locales.options[PaymentCardType.PHYSICAL].title,
                  {
                    PREMIUM_CARD_NAME,
                  },
                ) as string
              }
            >
              <SunshineCard.Slot name="aside">
                <Image
                  alt=""
                  opacity={canRequestPhysicalCard ? 1 : 0.5}
                  role="presentation"
                  src={physicalPremiumRebrandedPaymentCardImg}
                  width={110}
                />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.options[PaymentCardType.PHYSICAL].description}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineIcon
                  color={canRequestPhysicalCard ? 'grey.600' : 'grey.500'}
                  name="chevron-right"
                  size="icon-20"
                />
              </SunshineCard.Slot>
            </SunshineCard>
          </SunshineCard.Group>

          {!canRequestPhysicalCard ? (
            <PhysicalCardQuotaReachedFaq
              cardNumber={firstPhysicalActivePaymentCardNumber}
              paddingTop="space-32"
            />
          ) : null}
        </Layout.BodyScrollableSection>
      </Layout.Body>
    </Layout.Root>
  );
};

export default SelectCard;
